import React, { useCallback, useEffect, useState } from "react";
import { FiCheck, FiX } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LoginForm from "./Login";
import ResetPassword from "./ResetPassword";
import Signup from "./Signup";
import VerifyEmail from "./VerifyEmail";
import VendorOrbisLogo from "../../assets/vendorOrbis.png";
import { useToast } from "../../components/Toast/ToastContext";
import { login } from "../../store/auth/actions";
import { AppDispatch, RootState } from "../../store/store";
import { hasPendingPlan } from "../../services/webBilling";
import { trackMetaEvent } from "../../services/metaPixel";
import {
  hideLoginModal,
  hideRegisterModal,
  showLoginModal,
  showRegisterModal,
} from "../../store/auth/login/actions";

const SELLING_POINTS = [
  "Take cash, card, Tap to Pay and QR payments",
  "See stock and profit for every show",
  "One account on iPhone, Android and the web",
];

const BrandPanel = ({ returning }: { returning: boolean }) => (
  <div className="relative hidden overflow-hidden bg-gradient-to-br from-blue-600/30 via-indigo-600/20 to-violet-600/30 p-8 md:flex md:flex-col">
    <div
      aria-hidden
      className="absolute inset-0 bg-[radial-gradient(rgba(148,163,184,0.18)_1px,transparent_1px)] [background-size:20px_20px] [mask-image:linear-gradient(to_bottom,black,transparent)]"
    />
    <img src={VendorOrbisLogo} alt="VendorOrbis" className="relative h-8 w-auto self-start" />
    <div className="relative mt-auto">
      <p className="text-2xl font-bold leading-tight text-white">
        {returning ? "Your booth, right where you left it." : "Run your booth from your pocket."}
      </p>
      <ul className="mt-6 space-y-3">
        {SELLING_POINTS.map((point) => (
          <li key={point} className="flex items-start gap-3 text-sm text-slate-200">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-400/20 text-emerald-300">
              <FiCheck size={12} />
            </span>
            {point}
          </li>
        ))}
      </ul>
      <p className="mt-8 text-xs text-slate-400">
        Free forever for up to 10 events and 25 items. No credit card.
      </p>
    </div>
  </div>
);

const AuthContainer = ({ onClose }: { onClose?: () => void }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [resetPasswordState, setResetPasswordState] = useState(false);
  // Set after sign-up, or when an unverified account tries to log in.
  const [verifying, setVerifying] = useState<{ email: string; password: string } | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useToast();

  const isLoginModalVisible = useSelector((state: RootState) => state.login.isLoginModalVisible);
  const isRegisterModalVisible = useSelector(
    (state: RootState) => state.login.isRegisterModalVisible
  );

  const startClosingModal = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      dispatch(hideLoginModal());
      dispatch(hideRegisterModal());
      if (["/my-uploads", "/profile"].includes(location.pathname)) navigate("/");
      onClose?.();
    }, 200);
  }, [dispatch, location.pathname, navigate, onClose]);

  useEffect(() => {
    setIsVisible(true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") startClosingModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [startClosingModal]);

  const switchToSignup = () => {
    setResetPasswordState(false);
    dispatch(hideLoginModal());
    dispatch(showRegisterModal());
  };
  const switchToLogin = () => {
    setResetPasswordState(false);
    dispatch(hideRegisterModal());
    dispatch(showLoginModal());
  };

  const handleVerified = async () => {
    if (!verifying) return;
    const session = await dispatch(login(verifying.email, verifying.password) as any);
    // Counts verified sign-ups only, the ones Meta should optimise for.
    const userId = session?.user?._id;
    trackMetaEvent("CompleteRegistration", {}, userId ? `signup-${userId}` : undefined);
    notify("Email verified. Welcome to VendorOrbis!", { type: "success" });
    setVerifying(null);
    // Picked a paid plan before signing up: go back to finish checkout.
    navigate(hasPendingPlan() ? "/pricing" : "/inventory");
    dispatch(hideLoginModal());
    dispatch(hideRegisterModal());
    onClose?.();
  };

  const returning = !verifying && !isRegisterModalVisible;

  let content: React.ReactNode = null;
  if (verifying) {
    content = (
      <VerifyEmail
        email={verifying.email}
        onVerified={handleVerified}
        onBack={() => {
          setVerifying(null);
          switchToSignup();
        }}
      />
    );
  } else if (resetPasswordState) {
    content = <ResetPassword onBackToLogin={() => setResetPasswordState(false)} />;
  } else if (isRegisterModalVisible) {
    content = (
      <Signup
        onNeedsVerification={(email, password) => setVerifying({ email, password })}
        onSwitchToLogin={switchToLogin}
      />
    );
  } else if (isLoginModalVisible) {
    content = (
      <LoginForm
        onClose={startClosingModal}
        onNeedsVerification={(email, password) => setVerifying({ email, password })}
        onForgotPassword={() => setResetPasswordState(true)}
        onSwitchToSignup={switchToSignup}
      />
    );
  }

  return (
    <div
      onClick={startClosingModal}
      className={`fixed inset-0 z-[1000] flex items-end justify-center bg-black/70 backdrop-blur-sm transition-opacity duration-200 sm:items-center sm:p-6 ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={isRegisterModalVisible ? "Create an account" : "Log in"}
        onClick={(e) => e.stopPropagation()}
        className={`relative grid max-h-[100dvh] w-full overflow-y-auto rounded-t-3xl border border-white/10 bg-[#0b1020] shadow-2xl shadow-black/60 transition duration-200 sm:max-h-[92vh] sm:max-w-[440px] sm:rounded-3xl md:max-w-[880px] md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] ${
          isVisible ? "translate-y-0 scale-100" : "translate-y-6 scale-[0.98]"
        }`}
      >
        <BrandPanel returning={returning} />
        <div className="px-6 pb-8 pt-8 sm:px-10 sm:pt-10">
          <img src={VendorOrbisLogo} alt="VendorOrbis" className="mb-6 h-7 w-auto md:hidden" />
          {content}
        </div>
        <button
          type="button"
          onClick={startClosingModal}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-white/10 hover:text-white"
        >
          <FiX size={18} />
        </button>
      </div>
    </div>
  );
};

export default AuthContainer;
